"use client";
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { RiMenuUnfoldLine } from 'react-icons/ri';
import { MdCancel } from 'react-icons/md';
import Quote from './Quote';

const Navlinks = [
  { id: 2, title: "Nosotros", link: "/about" },
  { 
    id: 3, 
    title: "ERP CIGE", 
    link: "/erpcige",
    sublinks: [
      { id: 1, title: "CIGE - ERP", link: "/erpcige/cigeerp" },
      { id: 2, title: "CIGE - FI", link: "/erpcige/cigefi" },
      { id: 3, title: "CIGE - CPE", link: "/erpcige/cigecpe" },
      { id: 4, title: "CIGE - Planillas", link: "/erpcige/cigeplanillas" }
    ]
  },
  { id: 4, title: "Servicios", link: "/services" },
  {
    id: 5, 
    title: "Soluciones TI", 
    link: "/itsolutions",
    sublinks: [
      { id: 1, title: "Microsoft Office", link: "/itsolutions/microsoftoffice" },
      { id: 2, title: "Certificado Digital FE", link: "/itsolutions/fedigitalcertificate" }
    ]
  },
  { id: 6, title: "SAPB1Reports", link: "/sapb1reports" },
  { id: 7, title: "Contacto", link: "/contact" },
];

const Navbar = () => {
  const [menu, setMenu] = useState(false);
  const [dropdown, setDropdown] = useState<number | null>(null);
  const [hovering, setHovering] = useState<boolean>(false);
  const router = useRouter();
  const dropdownRef = useRef<HTMLUListElement>(null);
  const timeoutId = useRef<NodeJS.Timeout | null>(null); // Usa useRef para timeoutId

  const handleChange = () => {
    setMenu(!menu);
  };

  const closeMenu = () => {
    setMenu(false);
    setDropdown(null);
  };

  const toggleDropdown = (id: number | null) => {
    setDropdown(id);
  };

  const handleLinkClick = (href: string) => {
    closeMenu();
    router.push(href);
  };

  const handleMouseEnter = (id: number) => {
    if (window.innerWidth > 768) {
      if (timeoutId.current) {
        clearTimeout(timeoutId.current); // Limpia el timeout si existe
      }
      setDropdown(id);
      setHovering(true);
    }
  };

  const handleMouseLeave = () => {
    if (window.innerWidth > 768) {
      setHovering(false);
      timeoutId.current = setTimeout(() => {
        if (!hovering) {
          setDropdown(null);
        }
      }, 200);
    }
  };

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      if (timeoutId.current) {
        clearTimeout(timeoutId.current); // Limpia el timeout al desmontar
      }
    };
  }, [hovering]);

  const renderSubmenu = (sublinks: { id: number; title: string; link: string }[] | undefined) => {
    if (!sublinks) return null;

    return (
      <ul ref={dropdownRef} className="submenu bg-blue text-white absolute left-0 mt-2 rounded-md shadow-lg z-10 min-w-[200px]">
        {sublinks.map((sublink) => (
          <li key={sublink.id} className="hover:bg-blue-light p-2 transition-colors duration-200">
            <Link href={sublink.link} onClick={() => handleLinkClick(sublink.link)}>
              {sublink.title}
            </Link>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <nav className="navbar bg-blue text-white sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-center">
        <div className="w-full flex items-center justify-between h-16">
          <div className="lg:hidden">
            <button
              onClick={handleChange}
              className="text-white p-2 rounded-md hover:bg-blue-light focus:outline-none transition-colors duration-200"
              aria-label="Toggle menu"
            >
              {menu ? <MdCancel className="text-3xl" /> : <RiMenuUnfoldLine className="text-3xl" />}
            </button>
          </div>

          <div className={`${menu ? 'block' : 'hidden'} lg:block absolute lg:relative top-16 lg:top-0 left-0 w-full lg:w-auto bg-blue lg:bg-transparent lg:justify-center`}>
            <ul className="flex flex-col lg:flex-row lg:items-center lg:space-x-6 p-4 lg:p-0">
              {Navlinks.map((navlink) => (
                <li
                  key={navlink.id}
                  className="relative group"
                  onMouseEnter={() => handleMouseEnter(navlink.id)}
                  onMouseLeave={handleMouseLeave}
                >
                  <Link
                    href={navlink.link}
                    onClick={() => handleLinkClick(navlink.link)}
                    className={`block py-2 px-4 rounded-md hover:bg-blue-light transition-colors duration-200 ${
                      router.pathname === navlink.link ? 'bg-blue-light' : ''
                    }`}
                  >
                    {navlink.title}
                  </Link>

                  {navlink.sublinks && (
                    <>
                      <button
                        onClick={() => toggleDropdown(navlink.id)}
                        className="lg:hidden absolute right-2 top-2 text-white p-2 focus:outline-none"
                      >
                        {dropdown === navlink.id ? '-' : '+'}
                      </button>
                      {dropdown === navlink.id && renderSubmenu(navlink.sublinks)}
                    </>
                  )}
                  {dropdown === navlink.id && window.innerWidth > 768 && renderSubmenu(navlink.sublinks)}
                </li>
              ))}
              <li className="ml-4">
                <Quote/>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
